import { ColumnDef } from '@tanstack/react-table';
import { ClockCounterClockwise, PencilSimple, Power } from '@phosphor-icons/react';
import { DataTableColumnHeader, StatusChip } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { formatDate } from '@/lib/utils/custom-date/custom-date';
import { MBM_ACTIVE_STATUS, MBM_INACTIVE_STATUS, MbmProject } from '../../types/mbm-project.types';

interface CreateMbmProjectTableColumnsProps {
  t: (key: string) => string;
  onEdit: (project: MbmProject) => void;
  onToggleStatus: (project: MbmProject) => void;
  onViewHistory: (project: MbmProject) => void;
  canUpdate?: boolean;
}

/** MBM sends free-form statuses, so anything that is not Active/Inactive is shown as it arrived. */
export const MbmProjectStatusBadge = ({
  status,
  t,
}: {
  status?: string;
  t: (key: string) => string;
}) => {
  if (status === MBM_ACTIVE_STATUS) return <StatusChip tone="success">{t('ACTIVE')}</StatusChip>;
  if (!status || status === MBM_INACTIVE_STATUS) {
    return <StatusChip tone="muted">{t('INACTIVE')}</StatusChip>;
  }
  return <StatusChip tone="info">{status}</StatusChip>;
};

export const createMbmProjectTableColumns = ({
  t,
  onEdit,
  onToggleStatus,
  onViewHistory,
  canUpdate = true,
}: CreateMbmProjectTableColumnsProps): ColumnDef<MbmProject>[] => [
  {
    id: 'ProjectName',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('PROJECT_NAME')} />,
    accessorFn: (row) => row.ProjectName ?? '',
    cell: ({ row }) => (
      <div className="flex min-w-0 flex-col">
        <span className="truncate font-medium">{row.original.ProjectName}</span>
        <span className="truncate text-xs text-muted-foreground">{row.original.ProjectId}</span>
      </div>
    ),
  },
  {
    id: 'CustomerName',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('CUSTOMER_NAME')} />,
    accessorFn: (row) => row.CustomerName ?? '',
    cell: ({ row }) => (
      <div className="flex min-w-0 flex-col">
        <span className="truncate">{row.original.CustomerName}</span>
        <span className="truncate text-xs text-muted-foreground">{row.original.CustomerId}</span>
      </div>
    ),
  },
  {
    id: 'Status',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('STATUS')} />,
    accessorFn: (row) => row.Status ?? '',
    cell: ({ row }) => <MbmProjectStatusBadge status={row.original.Status} t={t} />,
  },
  {
    id: 'LastUpdatedDate',
    meta: { filter: { type: 'datetime' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('LAST_MODIFIED')} />,
    accessorFn: (row) => row.LastUpdatedDate ?? '',
    cell: ({ row }) =>
      row.original.LastUpdatedDate ? formatDate(new Date(row.original.LastUpdatedDate), true) : '-',
  },
  {
    id: 'actions',
    header: () => <span className="text-xs font-medium">{t('ACTION')}</span>,
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => {
      const isActive = row.original.Status === MBM_ACTIVE_STATUS;
      return (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onViewHistory(row.original)}
            aria-label={t('MBM_PROJECT_HISTORY')}
          >
            <ClockCounterClockwise className="size-4" />
          </Button>
          {canUpdate && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onEdit(row.original)}
                aria-label={t('EDIT')}
              >
                <PencilSimple className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onToggleStatus(row.original)}
                aria-label={isActive ? t('DEACTIVATE') : t('ACTIVATE')}
              >
                <Power className="size-4" />
              </Button>
            </>
          )}
        </div>
      );
    },
  },
];
